/**
 * Account resolution: which persistent account does a statement belong to?
 *
 * The four-rung ladder from the engagement-analysis PRD, as a pure function.
 * The caller (the processing-callback repository) queries the workspace's
 * active accounts, resolves the institution key against the bank registry,
 * calls this, and persists whatever this decides. Nothing here does IO.
 *
 * Near misses never merge. When institution and last4 agree but account name
 * or currency differ, the answer is "create, and name the candidate" so a
 * person can merge from the Accounts screen. Merging the wrong two accounts
 * corrupts a whole engagement invisibly; an extra account is a visible,
 * fixable nuisance.
 */

/** What a statement says about its own account identity. */
export type StatementAccountIdentity = {
  readonly institutionName: string | null
  /** Resolved against the bank registry by the caller; null when unknown. */
  readonly institutionKey: string | null
  readonly accountName: string | null
  readonly accountNumberLast4: string | null
  readonly currency: string | null
}

/** An existing active account in the workspace, oldest first. */
export type AccountCandidate = {
  readonly id: string
  readonly institutionName: string
  readonly institutionKey: string | null
  readonly accountName: string | null
  readonly accountNumberLast4: string | null
  readonly currency: string | null
}

export type AccountResolutionOutcome =
  | { readonly kind: "matched"; readonly accountId: string; readonly rung: 1 | 2 | 3 }
  | { readonly kind: "create"; readonly nearMiss: AccountCandidate | null }
  /** Not enough identity to name an account at all; the document still lands. */
  | { readonly kind: "unresolvable" }

const LEGAL_SUFFIXES = [
  /,?\s+national association$/,
  /,?\s+n\.?\s?a\.?$/,
  /,?\s+f\.?s\.?b\.?$/,
  /,?\s+s\.?s\.?b\.?$/,
  /,?\s+inc\.?$/,
]

/**
 * Lowercase, punctuation to spaces, collapsed whitespace, trailing legal
 * boilerplate ("N.A.", "National Association") dropped. "Northstar Community
 * Bank, N.A." and "NORTHSTAR COMMUNITY BANK" resolve to the same key.
 */
export const normalizeInstitutionName = (
  value: string | null | undefined
): string | null => {
  if (!value) return null
  let name = value.trim().toLowerCase()
  for (const suffix of LEGAL_SUFFIXES) {
    name = name.replace(suffix, "")
  }
  name = name.replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim()
  return name.length > 0 ? name : null
}

export const normalizeAccountName = (
  value: string | null | undefined
): string | null => {
  if (!value) return null
  const name = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
  return name.length > 0 ? name : null
}

const normalizeLast4 = (value: string | null | undefined): string | null => {
  if (!value) return null
  const digits = value.replace(/\D+/g, "")
  if (digits.length === 0) return null
  return digits.slice(-4)
}

/**
 * A usable ISO 4217 code, or null. Extraction sometimes emits junk here
 * ("$", "US"); treating it as unknown lets the compatibility rungs match
 * instead of minting a duplicate account over a currency symbol.
 */
export const normalizeCurrencyCode = (
  value: string | null | undefined
): string | null => {
  const currency = value?.trim().toUpperCase() ?? ""
  return /^[A-Z]{3}$/.test(currency) ? currency : null
}

const normalizeCurrency = normalizeCurrencyCode

/** Equal after normalization, treating null as equal to null (strict tuple). */
const currencyEqual = (a: string | null, b: string | null): boolean =>
  normalizeCurrency(a) === normalizeCurrency(b)

/** Equal after normalization, or unknown on either side (compatible). */
const currencyCompatible = (a: string | null, b: string | null): boolean => {
  const left = normalizeCurrency(a)
  const right = normalizeCurrency(b)
  return left === null || right === null || left === right
}

const accountNameCompatible = (a: string | null, b: string | null): boolean => {
  const left = normalizeAccountName(a)
  const right = normalizeAccountName(b)
  return left === null || right === null || left === right
}

/**
 * Resolve a statement to an existing account, or decide to create one.
 *
 * `candidates` are the workspace's active (unarchived) accounts, oldest
 * first; on a multi-match the oldest wins so the outcome is deterministic.
 */
export const resolveStatementAccount = (
  identity: StatementAccountIdentity,
  candidates: readonly AccountCandidate[]
): AccountResolutionOutcome => {
  const institutionKey = identity.institutionKey?.trim() || null
  const institutionName = normalizeInstitutionName(identity.institutionName)
  const accountName = normalizeAccountName(identity.accountName)
  const last4 = normalizeLast4(identity.accountNumberLast4)

  if (!institutionKey && !institutionName) {
    return { kind: "unresolvable" }
  }

  const sameInstitution = (candidate: AccountCandidate): boolean =>
    (institutionKey !== null && candidate.institutionKey === institutionKey) ||
    (institutionName !== null &&
      normalizeInstitutionName(candidate.institutionName) === institutionName)

  // Rung 1: exact on (institution_key, last4, currency), with the account
  // name compatible. Currency is part of the tuple, so null matches null.
  if (institutionKey && last4) {
    const match = candidates.find(
      (candidate) =>
        candidate.institutionKey === institutionKey &&
        normalizeLast4(candidate.accountNumberLast4) === last4 &&
        currencyEqual(candidate.currency, identity.currency) &&
        accountNameCompatible(candidate.accountName, identity.accountName)
    )
    if (match) return { kind: "matched", accountId: match.id, rung: 1 }
  }

  // Rung 2: exact on (normalized institution name, last4). The PRD scopes
  // this to institutions missing from the registry; it also runs after a
  // failed rung 1 so an account created before the registry knew its bank
  // (institution_key null) still matches instead of minting a duplicate.
  if (institutionName && last4) {
    const match = candidates.find(
      (candidate) =>
        normalizeInstitutionName(candidate.institutionName) === institutionName &&
        normalizeLast4(candidate.accountNumberLast4) === last4 &&
        accountNameCompatible(candidate.accountName, identity.accountName) &&
        currencyCompatible(candidate.currency, identity.currency)
    )
    if (match) return { kind: "matched", accountId: match.id, rung: 2 }
  }

  // Rung 3: no last4 at all. Without this, every statement from a bank that
  // omits the account number would mint a new account.
  if (!last4 && institutionName && accountName) {
    const match = candidates.find(
      (candidate) =>
        normalizeInstitutionName(candidate.institutionName) === institutionName &&
        normalizeAccountName(candidate.accountName) === accountName &&
        currencyCompatible(candidate.currency, identity.currency)
    )
    if (match) return { kind: "matched", accountId: match.id, rung: 3 }
  }

  // Rung 4: create. When institution and last4 agree with an existing
  // account but name or currency kept it from matching, that candidate is a
  // near miss worth flagging, never silently merging.
  const nearMiss =
    last4 !== null
      ? candidates.find(
          (candidate) =>
            sameInstitution(candidate) &&
            normalizeLast4(candidate.accountNumberLast4) === last4
        )
      : undefined

  return { kind: "create", nearMiss: nearMiss ?? null }
}

/**
 * Match-only resolution for documents that identify an account without being
 * a statement (a check: bank name plus last4). Never creates: a check does
 * not carry enough identity to mint an account worth keeping.
 */
export const matchAccountForReference = (
  identity: Pick<
    StatementAccountIdentity,
    "institutionName" | "accountNumberLast4"
  >,
  candidates: readonly AccountCandidate[]
): string | null => {
  const institutionName = normalizeInstitutionName(identity.institutionName)
  const last4 = normalizeLast4(identity.accountNumberLast4)
  if (!last4) return null

  const byInstitution = institutionName
    ? candidates.filter(
        (candidate) =>
          normalizeInstitutionName(candidate.institutionName) === institutionName
      )
    : candidates

  const matches = byInstitution.filter(
    (candidate) => normalizeLast4(candidate.accountNumberLast4) === last4
  )

  // Only an unambiguous match counts: two accounts sharing a last4 means the
  // check cannot say which one it drew on.
  return matches.length === 1 ? matches[0]!.id : null
}
